/**
 * IntegrationDrawer — side panel for connect / settings / sync history.
 *
 * Renders three tabs:
 *   Setup       — provider description + numbered steps + credentials form
 *   Settings    — auto-sync toggle + frequency dial
 *   Sync history — last 20 entries from /api/integrations/syncs
 *
 * On Connect we POST to /api/integrations/connect; on Disconnect we POST
 * /disconnect; sync history fetches /syncs?provider=…
 */
import React, { useEffect, useState } from "react";
import { X, Loader2, Plug, Save, RefreshCw, Power, AlertOctagon, CheckCircle2 } from "lucide-react";
import { BACKEND_URL } from "../../lib/backendUrl";

const API = `${BACKEND_URL}/api/integrations`;

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = (Date.now() - t) / 1000;
  if (diff < 60)    return "Just now";
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleString();
}

export default function IntegrationDrawer({ providerId, customerId, row, onClose, onChanged }) {
  const def = row?.definition;
  const [tab, setTab] = useState("setup");
  const [creds, setCreds] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [autoSync, setAutoSync] = useState(row?.auto_sync ?? true);
  const [freq, setFreq] = useState(row?.sync_frequency_hours ?? def?.default_frequency_hours ?? 6);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    // Reset creds form whenever drawer opens for a new provider.
    const init = {};
    (def?.credentials || []).forEach((c) => {
      init[c.key] = c.default || "";
    });
    setCreds(init);
    setError(null); setSuccess(null);
  }, [providerId, def]);

  useEffect(() => {
    if (tab !== "history") return;
    let alive = true;
    (async () => {
      setHistoryLoading(true);
      try {
        const r = await fetch(`${API}/syncs?provider=${providerId}&limit=20`, { credentials: "include" });
        const j = await r.json();
        if (alive) setHistory(j.syncs || []);
      } catch (_e) { /* silent */ }
      finally { if (alive) setHistoryLoading(false); }
    })();
    return () => { alive = false; };
  }, [tab, customerId, providerId]);

  if (!def) return null;
  const connected = row?.status === "connected";

  const connect = async () => {
    setBusy(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/connect`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider: providerId, credentials: creds }),
      });
      const j = await r.json();
      if (!r.ok) {
        const d = j?.detail;
        const missing = (d && typeof d === "object" && d.missing) ? ` Missing: ${d.missing.join(", ")}` : "";
        throw new Error((typeof d === "string" ? d : d?.error || "Connect failed") + missing);
      }
      setSuccess("Connected successfully. You can resync from the card.");
      onChanged && onChanged();
    } catch (e) { setError(e.message || "Connect failed"); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/disconnect`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider: providerId }),
      });
      if (!r.ok) throw new Error("Disconnect failed");
      setSuccess("Disconnected.");
      onChanged && onChanged();
    } catch (e) { setError(e.message || "Disconnect failed"); }
    finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setBusy(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/settings`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: providerId,
          auto_sync: autoSync, sync_frequency_hours: Number(freq),
        }),
      });
      if (!r.ok) throw new Error("Settings save failed");
      setSuccess("Settings saved.");
      onChanged && onChanged();
    } catch (e) { setError(e.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70]" data-testid={`drawer-${providerId}`}>
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col overflow-y-auto bg-white shadow-xl">
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{def.category}</div>
            <h2 className="text-[16px] font-bold text-[#0F1B2D]">{def.name}</h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-slate-100" data-testid={`drawer-close-${providerId}`}>
            <X size={16} />
          </button>
        </header>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          {[
            ["setup",    "Setup"],
            ["settings", "Settings"],
            ["history",  "Sync history"],
          ].map(([id, label]) => (
            <button
              key={id} onClick={() => setTab(id)}
              data-testid={`drawer-tab-${id}`}
              className={`px-4 py-2 text-[12px] font-semibold ${
                tab === id ? "border-b-2 border-emerald-600 text-emerald-700" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 p-5">
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
              <AlertOctagon size={13} className="mt-0.5" /> {error}
            </div>
          )}
          {success && (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-700">
              <CheckCircle2 size={13} className="mt-0.5" /> {success}
            </div>
          )}

          {tab === "setup" && (
            <div className="space-y-4">
              <p className="text-[12.5px] text-slate-600">{def.blurb}</p>

              {!def.live && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-800">
                  This integration's sync engine is being finalised — you can save your credentials now and we'll auto-sync as soon as the connector ships.
                </div>
              )}

              <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 text-[12px]">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">How to set up</div>
                <ol className="mt-2 space-y-1.5 text-slate-700">
                  {(def.setup_steps || []).map((s, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </div>

              {providerId === "tally" && (
                <div
                  data-testid="tally-download-cta"
                  className="rounded-md border border-emerald-200 bg-emerald-50 p-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[12px] font-bold text-emerald-900">
                        Universal C-ORE Tally Agent
                        <span className="ml-2 inline-flex items-center rounded-full bg-emerald-600 px-2 py-0.5 text-[9.5px] font-bold text-white">
                          v1.2.2
                        </span>
                      </div>
                      <div className="mt-1 text-[11.5px] text-emerald-800">
                        Works on Tally ERP 9 + Prime 2 / 3 / 4. Detects your version on first run.
                      </div>
                    </div>
                    <a
                      data-testid="tally-download-btn"
                      href={`${BACKEND_URL}/api/tally/install-kit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[11.5px] font-semibold text-white shadow-sm transition hover:bg-emerald-700"
                    >
                      Download (.zip)
                    </a>
                  </div>
                  <div className="mt-2 text-[10.5px] leading-relaxed text-emerald-900/80">
                    Need your personal bridge token pre-filled?
                    Ask your CSM to email you the customised installer
                    (admin → Setup checklist → "Send Tally install email").
                  </div>
                </div>
              )}

              {/* Zoho uses OAuth — replace the credentials list + Connect button
                  with a single "Connect via Zoho" CTA that redirects through
                  the consent screen. The backend captures the refresh token
                  automatically on callback. */}
              {providerId === "zoho" && !connected && (
                <div
                  data-testid="zoho-oauth-cta"
                  className="rounded-md border border-blue-200 bg-blue-50 p-3"
                >
                  <div className="text-[12px] font-bold text-blue-900">
                    Zoho uses OAuth 2.0
                  </div>
                  <div className="mt-1 text-[11.5px] text-blue-800">
                    Click below to authorize C-ORE on accounts.zoho.in. We'll
                    capture a long-lived refresh token automatically — no
                    code-pasting needed.
                  </div>
                  <a
                    data-testid="zoho-connect-oauth-btn"
                    href={
                      customerId
                        ? `${BACKEND_URL}/api/zoho/start?customer_id=${encodeURIComponent(customerId)}&return_to=/integrations`
                        : "#"
                    }
                    aria-disabled={!customerId}
                    onClick={(e) => { if (!customerId) e.preventDefault(); }}
                    className={`mt-3 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-[12.5px] font-semibold text-white ${
                      customerId
                        ? "bg-blue-600 hover:bg-blue-700"
                        : "bg-slate-400 cursor-not-allowed"
                    }`}
                  >
                    Connect via Zoho →
                  </a>
                </div>
              )}

              {(def.credentials || []).length > 0 && providerId !== "zoho" && (
                <div className="space-y-2">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500">Credentials</div>
                  {def.credentials.map((c) => (
                    <div key={c.key}>
                      <label className="block text-[11.5px] font-semibold text-slate-700">
                        {c.label}{c.required && <span className="text-rose-500"> *</span>}
                      </label>
                      <input
                        data-testid={`drawer-cred-${c.key}`}
                        type={c.secret ? "password" : "text"}
                        value={creds[c.key] || ""}
                        onChange={(e) => setCreds((p) => ({ ...p, [c.key]: e.target.value }))}
                        placeholder={c.secret ? "•••••" : c.label}
                        disabled={busy || !customerId}
                        className="mt-1 w-full rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px] focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                {connected ? (
                  <button
                    data-testid="drawer-disconnect"
                    onClick={disconnect}
                    disabled={busy || !customerId}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-rose-300 bg-white px-3 py-2 text-[12.5px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                    Disconnect
                  </button>
                ) : providerId === "zoho" ? null : (
                  <button
                    data-testid="drawer-connect"
                    onClick={connect}
                    disabled={busy || !customerId}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Plug size={12} />}
                    Connect
                  </button>
                )}
              </div>

              {!customerId && (
                <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11.5px] text-blue-800">
                  Sign up to save credentials and start syncing.
                </div>
              )}
            </div>
          )}

          {tab === "settings" && (
            <div className="space-y-4">
              <div>
                <label className="flex items-center justify-between text-[12.5px] font-semibold text-slate-800">
                  <span>Auto-sync</span>
                  <button
                    data-testid="drawer-autosync-toggle"
                    onClick={() => setAutoSync((v) => !v)}
                    className={`relative h-5 w-9 rounded-full transition-colors ${autoSync ? "bg-emerald-600" : "bg-slate-300"}`}
                  >
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${autoSync ? "translate-x-4" : "translate-x-0.5"}`} />
                  </button>
                </label>
                <p className="mt-1 text-[11px] text-slate-500">
                  Scheduled syncs run automatically when enabled.
                </p>
              </div>

              <div>
                <label className="block text-[11.5px] font-semibold text-slate-700">Sync frequency (hours)</label>
                <div className="mt-1 inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                  {[1, 6, 12, 24].map((h) => (
                    <button
                      key={h}
                      data-testid={`drawer-freq-${h}`}
                      onClick={() => setFreq(h)}
                      className={`rounded px-3 py-1.5 text-[12px] font-semibold ${
                        Number(freq) === h ? "bg-[#0F1B2D] text-white" : "text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {h}h
                    </button>
                  ))}
                </div>
              </div>

              <button
                data-testid="drawer-save-settings"
                onClick={saveSettings}
                disabled={busy || !customerId}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                Save settings
              </button>
            </div>
          )}

          {tab === "history" && (
            <div data-testid="drawer-history">
              {historyLoading ? (
                <div className="flex items-center gap-2 text-[12.5px] text-slate-500">
                  <Loader2 size={13} className="animate-spin" /> Loading…
                </div>
              ) : history.length === 0 ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-center text-[12.5px] text-slate-500">
                  No syncs yet. Hit <span className="font-semibold">Connect</span> to start, then <RefreshCw size={11} className="inline" /> resync from the card.
                </div>
              ) : (
                <ul className="space-y-2">
                  {history.map((h) => (
                    <li key={h.id} className="rounded-md border border-slate-200 bg-white p-2.5 text-[12px]">
                      <div className="flex items-center justify-between">
                        <span className={`font-semibold ${
                          h.status === "success" ? "text-emerald-700" :
                          h.status === "partial" ? "text-amber-700"   : "text-rose-700"}`}>
                          {h.status}
                        </span>
                        <span className="text-[11px] text-slate-500 tabular-nums">{timeAgo(h.started_at)}</span>
                      </div>
                      <div className="mt-0.5 text-[11.5px] text-slate-600">
                        {(h.records_synced || 0).toLocaleString("en-IN")} records
                        {h.duration_ms ? ` · ${h.duration_ms}ms` : ""}
                      </div>
                      {h.error && <div className="mt-1 truncate text-[11px] text-rose-600">{h.error}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
